import React, { useCallback, useContext, useMemo, useEffect, useRef } from 'react'
import dagre from 'dagre'
import { DAGTask, Recipe } from '@pollination-solutions/pollination-sdk'
import { TaskNode } from '../nodes/TaskNode'
import { FlowInputs } from '../nodes/FlowInputs'
import { FlowOutputs } from '../nodes/FlowOutputs'
import { GraphEdges } from '../edges/GraphEdges'
import { RootContext as context } from '~/state'
import { locate } from '~/utils'
import { usePanControls } from '~/hooks'

export interface RecipeViewerProps {
  recipe: Recipe
}

export const RecipeViewer = ({ recipe }: RecipeViewerProps): JSX.Element => {
  const { meta, layout, registerRoot, registerRecipe, pan } = useContext(context)

  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (layout?.root) {
      return
    }
    registerRoot(rootRef)
  }, [layout, registerRoot])

  useEffect(() => {
    if (meta?.recipe) {
      return
    }
    registerRecipe(recipe)
  }, [meta, registerRecipe, recipe])

  const { transform } = usePanControls(rootRef, false)

  useEffect(() => {
    const [tx, ty] = layout?.position ?? [0, 0]
    const [cx, cy] = layout?.center ?? [0, 0]
    const [dx, dy] = transform

    if (dx + cx === tx && dy + cy === ty) {
      return
    }

    pan(dx + cx - tx, dy + cy - ty)
  }, [layout, pan, transform])

  const main = meta?.activeFlow ?? locate.recipeEntrypoint(recipe.flow).main

  // const { main } = useMemo(() => locate.recipeEntrypoint(recipe.flow), [recipe])

  // Add nodes and edges for each flow task to dagre graph
  const linkTasks = useCallback((graph: dagre.graphlib.Graph, tasks: DAGTask[], prefix: string): void => {
    tasks.forEach((task) => {
      // Create unique flow-scoped key for the given task
      const key = `${prefix}-task-${task.name}`

      // Add node for task to dagre graph object
      graph.setNode(key, {
        label: key,
        width: 300,
        height: 40,
        nodeType: 'task',
        task: task,
      })

      // Assign task node as child of flow cluster
      graph.setParent(key, prefix)

      // Create edges between task and its dependencies
      task.needs?.forEach((dependency) => {
        // Make sure the dependency exists in the current flow
        const upstreamTask = tasks.find((t) => t.name === dependency)
        if (!upstreamTask) {
          // Task declares a dependency not provided to the frontend, do not create orphaned edge.
          return
        }

        // Create unique flow-scoped key for located task
        const dependencyKey = `${prefix}-task-${upstreamTask.name}`

        // Add edge to dagre graph object
        graph.setEdge(dependencyKey, key)
      })
    })
  }, [])

  // Create initial dagre-based layout for recipe flow
  const graph = useMemo(() => {
    // Create dagre graph object
    const graph = new dagre.graphlib.Graph({ compound: true })

    // Parse config for any non-default values
    const direction = layout?.direction ?? 'LR'

    // Apply graph configuration
    graph.setGraph({ rankdir: direction, ranksep: 100, marginx: 100 })
    graph.setDefaultEdgeLabel(() => {
      return {}
    })

    // Create cluster node for flow
    graph.setNode(main.name, { label: main.name, nodeType: 'cluster' })

    // Link dag tasks
    linkTasks(graph, main.tasks, main.name)

    // Create layout with dagre
    dagre.layout(graph)

    return graph
  }, [main, layout, linkTasks])

  // Create task node elements from dagre layout
  const nodes = useMemo(() => {
    return graph.nodes().map((n) => {
      const { x, y, task } = graph.node(n) as any

      if (!task) {
        return
      }

      return <TaskNode key={`task-node-${task.name}`} position={[x, y]} task={task} />
    })
  }, [graph])

  // Create flow input nodes from dagre layout
  const inputs = useMemo(() => {
    const nodes = graph.nodes().map((n) => graph.node(n))

    const cluster = nodes.find((node: any) => node.nodeType === 'cluster' && node.label === main.name)

    if (!cluster) {
      return
    }

    const { x, y, width, height } = cluster

    return <FlowInputs position={[x, y]} size={[width, height]} inputs={main.inputs ?? []} />
  }, [graph, main])

  // Create flow output nodes from dagre layout
  const outputs = useMemo(() => {
    const nodes = graph.nodes().map((n) => graph.node(n))

    const cluster = nodes.find((node: any) => node.nodeType === 'cluster' && node.label === main.name)

    if (!cluster) {
      return
    }

    const { x, y, width, height } = cluster

    return <FlowOutputs position={[x, y]} size={[width, height]} outputs={main.outputs ?? []} tasks={main.tasks} />
  }, [graph, main])

  return (
    <div className="recipe-root" ref={rootRef}>
      {nodes}
      {inputs}
      {outputs}
      <GraphEdges />
      <style jsx>{`
        .recipe-root {
          position: relative;
          width: 100%;
          height: 100%;
          overflow: hidden;
        }
      `}</style>
    </div>
  )
}
